import { CircleAlert, ImageOff, NotebookPen, Printer, ScrollText } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { dashboardApi } from "../api";
import { useT } from "../i18n";
import { useServiceText } from "../service-text";
import type { ReviewData } from "../types";
import { formatCredits, formatDate, monthLabel } from "../utils";
import Amount from "../components/Amount";
import { TableSkeleton } from "../components/Skeleton";

/**
 * The quarterly review pack (backlog E6).
 *
 * It ASSEMBLES rather than calculates. Every figure comes from the same
 * `getBalance` the dashboard and the statement use, summed across the quarter's
 * months on the server — so this page and the ledger cannot disagree. It is the
 * version that ends up in front of the client, which is exactly why it must not
 * have opinions of its own about the money.
 *
 * It prints rather than downloading a server-rendered PDF. The statement earns its
 * PDF because it is a document a finance team files; a review is read on a screen
 * in a room, and the browser's own print already produces a real file from these
 * styles. Building a second PDF renderer would be a second place the arithmetic
 * could drift, for a format nobody asked for yet.
 */
export default function ReviewPage({ month }: { month: string }) {
  const t = useT();
  const st = useServiceText();
  const [quarter, setQuarter] = useState(() => quarterOf(month));
  const [data, setData] = useState<ReviewData | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (which: string) => {
    setLoading(true);
    try {
      setData(await dashboardApi.review(which));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t("error.buildReview"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(quarter); }, [load, quarter]);

  if (loading && !data) {
    return <div className="page-stack"><TableSkeleton rows={4} label={t("common.loading")} /></div>;
  }
  if (!data) return null;

  const { pack } = data;
  const outside = pack.monthsOutsideContract;

  return (
    <div className="page-stack review-page">
      <section className="catalog-summary">
        <div>
          <span className="section-eyebrow">{t("review.eyebrow")}</span>
          <h2>{t("review.title")}</h2>
          <p>{t("review.intro")}</p>
        </div>
        <div className="statement-actions no-print">
          <label className="filter-control">
            <span className="sr-only">{t("review.quarter")}</span>
            <select value={quarter} onChange={(event) => setQuarter(event.target.value)}>
              {/* Only quarters the contract touches. A picker that can select one
                  outside it produces an empty screen and no explanation. */}
              {data.quarters.map((option) => <option key={option} value={option}>{option}</option>)}
            </select>
          </label>
          <button type="button" className="button button-secondary" onClick={() => window.print()}>
            <Printer size={16} /> {t("common.print")}
          </button>
        </div>
      </section>

      {/* Said, never hidden. A zero-filled month outside the contract would read as
          a quarter where nothing happened rather than one that had not started. */}
      {outside.length > 0 && (
        <div className={`review-notice${pack.months.length ? "" : " review-notice-hard"}`}>
          <CircleAlert size={17} />
          <span>
            {pack.months.length
              ? t("review.partialQuarter", { months: outside.map(monthLabel).join(", ") })
              : t("review.outsideContract")}
          </span>
        </div>
      )}

      {pack.months.length > 0 && (
        <>
          <section className="metric-grid">
            {/* Carried-in is its OWN figure, never folded into "granted". A
                rollover is last month's leftover appearing again, not new money;
                summing the monthly pools printed 353 credits granted against a
                69-a-month contract. */}
            <Metric label={t("review.carriedIn")} value={pack.totals.carriedIn} />
            <Metric label={t("review.granted")} value={pack.totals.granted} />
            <Metric label={t("review.spent")} value={pack.totals.spent} />
            <Metric label={t("review.committed")} value={pack.totals.committed} />
            <Metric label={t("review.closing")} value={pack.totals.closing} />
          </section>
          <p className="record-meta">{t("review.grantedNote")}</p>

          {/* The statement's rule: when the parts do not rebuild the ledger's own
              closing figure, show the disagreement instead of a confident total. */}
          {!pack.totals.reconciled && (
            <div className="review-notice">
              <CircleAlert size={17} />
              <span>{t("review.unreconciled", {
                closing: formatCredits(pack.totals.closing),
                drift: formatCredits(pack.totals.drift),
              })}</span>
            </div>
          )}

          <section className="panel">
            <h3>{t("review.byMonth")}</h3>
            <p className="record-meta">{t("review.monthPoolNote")}</p>
            <div className="responsive-table-wrap">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>{t("common.month")}</th>
                    {/* POOL, not "granted". A month's pool includes what carried
                        into it, so labelling the column "granted" is the same
                        double-count the headline figure just stopped making. */}
                    <th>{t("review.monthPool")}</th>
                    <th>{t("review.spent")}</th>
                    <th>{t("review.closing")}</th>
                  </tr>
                </thead>
                <tbody>
                  {pack.byMonth.map((row) => (
                    <tr key={row.month}>
                      <td data-label={t("common.month")}>{monthLabel(row.month)}</td>
                      <td data-label={t("review.monthPool")}><Amount value={row.granted} unit={t("common.cr")} /></td>
                      <td data-label={t("review.spent")}><Amount value={row.spent} unit={t("common.cr")} /></td>
                      <td data-label={t("review.closing")}><Amount value={row.available} unit={t("common.cr")} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section className="panel">
            <h3>{t("review.delivered")}</h3>
            {pack.delivered.length ? (
              <>
                <p className="record-meta">{t("review.deliveredCount", { n: pack.delivered.length })}</p>
                <div className="review-grid">
                  {pack.delivered.map((item) => (
                    <article key={item.id} className="review-card">
                      <div className="review-card-image">
                        {item.cover_url
                          ? <img src={item.cover_url} alt="" loading="lazy" />
                          // The house placeholder, not a broken frame. Delivered work
                          // with no image attached is normal; a grey box reads as a
                          // failed load.
                          : <span className="review-card-placeholder"><ImageOff size={20} /></span>}
                      </div>
                      <div className="review-card-copy">
                        <strong dir="auto">{item.title}</strong>
                        <span dir="auto">{st.name(item.service_id, item.service_name)}</span>
                        <em>
                          <Amount value={item.credits} unit={t("common.cr")} />
                          {item.completed_at ? ` · ${formatDate(item.completed_at)}` : ""}
                        </em>
                      </div>
                    </article>
                  ))}
                </div>
              </>
            ) : (
              <div className="empty-state"><ImageOff size={26} /><h4>{t("review.nothingDelivered")}</h4></div>
            )}
          </section>

          <section className="panel">
            <h3>{t("review.byCategory")}</h3>
            {pack.byCategory.length ? (
              <div className="category-bars">
                {pack.byCategory.map((row) => (
                  <div className="category-bar-row" key={row.category}>
                    <div>
                      <span>{st.category(row.category)}</span>
                      <strong><Amount value={row.credits} unit={t("common.cr")} /></strong>
                    </div>
                    {/* .bar-track is the house bar, already themed. A second bar
                        class would drift from the overview's within a release. */}
                    <div className="bar-track">
                      <span style={{ inlineSize: `${percent(row.credits, pack.byCategory[0].credits)}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="record-meta">{t("review.noSpendCategories")}</p>
            )}
          </section>

          {pack.stillOpen.length > 0 && (
            <section className="panel">
              <h3>{t("review.stillOpen")}</h3>
              <p className="record-meta">{t("review.stillOpenBody")}</p>
              <ul className="document-list">
                {pack.stillOpen.map((row) => (
                  <li key={row.id}>
                    <strong dir="auto">{row.title}</strong>
                    <span className="record-meta">
                      {monthLabel(row.billing_month)} · <Amount value={row.credits} unit={t("common.cr")} />
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          <section className="panel">
            <h3>{t("meetings.title")}</h3>
            {pack.meetings.length ? (
              <ol className="record-list">
                {pack.meetings.map((meeting) => (
                  <li key={meeting.id}>
                    <div className="record-when">{formatDate(meeting.held_on)}</div>
                    <div className="record-body">
                      <strong dir="auto">{meeting.title}</strong>
                      <p dir="auto">{meeting.summary}</p>
                    </div>
                  </li>
                ))}
              </ol>
            ) : (
              <div className="empty-state"><NotebookPen size={26} /><h4>{t("meetings.emptyTitle")}</h4></div>
            )}
          </section>

          <section className="panel">
            <h3>{t("decisions.title")}</h3>
            {pack.decisions.length ? (
              <ol className="record-list">
                {pack.decisions.map((decision) => (
                  <li key={decision.id}>
                    <div className="record-when">{formatDate(decision.decided_on)}</div>
                    <div className="record-body">
                      <strong dir="auto">{decision.title}</strong>
                      <p dir="auto">{decision.rationale}</p>
                      {decision.request_title && (
                        <span className="record-meta">
                          {t("decisions.aboutRequest")} <span dir="auto">{decision.request_title}</span>
                          {decision.request_credits !== null && (
                            <> · <Amount value={decision.request_credits} unit={t("common.cr")} /></>
                          )}
                        </span>
                      )}
                    </div>
                  </li>
                ))}
              </ol>
            ) : (
              <div className="empty-state"><ScrollText size={26} /><h4>{t("decisions.emptyTitle")}</h4></div>
            )}
          </section>
        </>
      )}
    </div>
  );
}

function Metric({ label, value }: { label: string; value: number }) {
  const t = useT();
  return (
    <article className="metric-card">
      <span>{label}</span>
      <strong><Amount value={value} unit={t("common.cr")} /></strong>
    </article>
  );
}

/** Bars are relative to the LARGEST row, not to the total: with nine categories a
 *  share-of-total bar is a sliver for every one of them and compares nothing. */
function percent(value: number, largest: number): number {
  if (!largest) return 0;
  return Math.max(3, Math.round((value / largest) * 100));
}

function quarterOf(month: string): string {
  const [year, monthNumber] = month.split("-").map(Number);
  return `${year}-Q${Math.floor((monthNumber - 1) / 3) + 1}`;
}
